/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle, AlertOctagon, Info, ShieldCheck, Phone, CalendarPlus, X, Check, Bell } from 'lucide-react';
import { cn } from '@/lib/utils';
import { acknowledgeAlert, dismissAlert, bookPrioritySlot } from '@/lib/agenticAi';

const SEVERITY_META = {
  critical: {
    color: 'text-red-700 dark:text-red-300 bg-red-500/10 border-red-500/40',
    stripe: 'bg-red-500',
    Icon: AlertOctagon,
    label: 'Emergenza',
  },
  high: {
    color: 'text-orange-700 dark:text-orange-300 bg-orange-500/10 border-orange-500/40',
    stripe: 'bg-orange-500',
    Icon: AlertTriangle,
    label: 'Alta priorità',
  },
  medium: {
    color: 'text-amber-700 dark:text-amber-300 bg-amber-500/10 border-amber-500/40',
    stripe: 'bg-amber-500',
    Icon: Bell,
    label: 'Attenzione',
  },
  low: {
    color: 'text-blue-700 dark:text-blue-300 bg-blue-500/10 border-blue-500/40',
    stripe: 'bg-blue-500',
    Icon: Info,
    label: 'Informazione',
  },
};

const STATUS_LABEL = {
  new: { text: 'Nuovo', variant: 'default' },
  acknowledged: { text: 'Preso in carico', variant: 'secondary' },
  actioned: { text: 'Azionato', variant: 'secondary' },
  dismissed: { text: 'Ignorato', variant: 'outline' },
};

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('it-IT', { dateStyle: 'short', timeStyle: 'short' });
  } catch { return iso; }
};

const AgenticAlertCard = ({ alert, onChanged }) => {
  const navigate = useNavigate();
  const meta = SEVERITY_META[alert.severity] || SEVERITY_META.low;
  const Icon = meta.Icon;
  const status = STATUS_LABEL[alert.status] || STATUS_LABEL.new;

  const isActive = alert.status === 'new' || alert.status === 'acknowledged';

  const handleAck = async () => {
    try {
      await acknowledgeAlert(alert.id);
      toast.success('Alert preso in carico');
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore');
    }
  };

  const handleDismiss = async () => {
    try {
      await dismissAlert(alert.id, 'Utente ha ignorato l\'alert');
      toast.success('Alert ignorato');
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore');
    }
  };

  const handleBook = async () => {
    try {
      const res = await bookPrioritySlot(alert.id);
      toast.success('Prenotazione priority avviata');
      if (res.redirect) navigate(res.redirect);
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore');
    }
  };

  const handle112 = () => {
    // On mobile this opens the dialer; on desktop it opens the tel: link
    window.location.href = 'tel:112';
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -6 }}
      transition={{ duration: 0.2 }}
    >
      <Card
        className={cn(
          'overflow-hidden relative',
          alert.status === 'dismissed' && 'opacity-60',
        )}
        data-testid={`agentic-alert-${alert.code}`}
      >
        <div className={cn('absolute left-0 top-0 bottom-0 w-1.5', meta.stripe)} />
        <div className="p-4 pl-5">
          <div className="flex items-start gap-3">
            <div
              className={cn(
                'h-10 w-10 rounded-full flex items-center justify-center flex-shrink-0',
                meta.color,
              )}
            >
              <Icon className="h-5 w-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-display font-semibold text-base">{alert.title}</h3>
                <Badge
                  variant="secondary"
                  className={cn('text-[10px] uppercase font-bold', meta.color)}
                >
                  {meta.label}
                </Badge>
                <Badge variant={status.variant} className="text-[10px]">
                  {status.text}
                </Badge>
              </div>
              <p className="text-sm text-foreground/80 mt-1 leading-relaxed">{alert.message}</p>
              {alert.suggested_action && (
                <div className="mt-2 rounded-lg bg-muted/40 p-2 text-xs text-muted-foreground flex items-start gap-1.5">
                  <ShieldCheck className="h-3.5 w-3.5 mt-0.5 flex-shrink-0 text-primary" />
                  {alert.suggested_action}
                </div>
              )}
              {alert.evidence?.length > 0 && (
                <details className="mt-2 text-xs">
                  <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
                    Dati che hanno attivato l'alert ({alert.evidence.length})
                  </summary>
                  <ul className="mt-1 pl-4 space-y-0.5 list-disc">
                    {alert.evidence.map((e) => (
                      <li key={e.id}>
                        {e.metric}: <strong>{e.value}</strong> {e.unit} · {formatDate(e.measured_at)}
                      </li>
                    ))}
                  </ul>
                </details>
              )}
              <div className="mt-1 text-[10px] text-muted-foreground">
                {formatDate(alert.created_at)}
              </div>
            </div>
          </div>

          {isActive && (
            <div className="mt-4 flex items-center gap-2 flex-wrap">
              {alert.action_type === 'contact_112' && (
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={handle112}
                  data-testid={`alert-${alert.id}-call-112`}
                >
                  <Phone className="h-3.5 w-3.5 mr-1" /> Chiama 112
                </Button>
              )}
              {(alert.action_type === 'book_priority' || alert.action_type === 'notify_doctor') && (
                <Button size="sm" onClick={handleBook} data-testid={`alert-${alert.id}-book`}>
                  <CalendarPlus className="h-3.5 w-3.5 mr-1" /> Prenota consulto priority
                </Button>
              )}
              {alert.status === 'new' && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleAck}
                  data-testid={`alert-${alert.id}-ack`}
                >
                  <Check className="h-3.5 w-3.5 mr-1" /> Ho capito
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={handleDismiss}
                data-testid={`alert-${alert.id}-dismiss`}
              >
                <X className="h-3.5 w-3.5 mr-1" /> Ignora
              </Button>
            </div>
          )}
        </div>
      </Card>
    </motion.div>
  );
};

export default AgenticAlertCard;
export { SEVERITY_META };
